import classNames from 'classnames'
import contractStyles from 'features/contracts/Contracts.module.scss'
import styles from './ProductStep.module.scss'
import { AddContractStepProps, AddContractSteps, ProductFormInputs } from 'features/contracts/add/types'
import FormButtons from 'features/contracts/components/form-buttons/FormButtons'
import { useForm, useWatch } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { useStoreSelector } from 'hooks/store'
import { useGetLanguage } from 'hooks/use-get-language'
import { useGetProductContentQuery } from 'store/queries/cms-api'
import { checkHasProduct, checkIsEarlyBird, defineProducts, getUsableProducts } from 'utils/products'
import { BlocksRenderer } from '@strapi/blocks-react-renderer'
import { useTranslation } from 'react-i18next'
import { Button, Heading, InlineMessage, Tag } from '@boltenergy-be/design-system'
import ProductDrawer from 'features/contracts/add/components/product-details-drawer/ProductDrawer'
import { selectCurrentAccount, selectCurrentBillingContract, selectHasPlentyProduct } from 'store/contact/slice.ts'
import {
  CUSTOMER_TYPES,
  EL_PRODUCT_CODES,
  ELProduct,
  GAS_PRODUCT_CODES,
  getProductFromProductCode,
  Product,
  PRODUCTS,
  REGIONS
} from '@boltenergy-be/shared'
import { ContractFlowTrackingTypes } from 'types/tracking.ts'

const ProductStep = ({ setNextStep, addContractData }: AddContractStepProps) => {
  // Redux
  const { userType } = useStoreSelector((store) => store.auth)
  const account = useStoreSelector(selectCurrentAccount)
  const billingContract = useStoreSelector(selectCurrentBillingContract)
  const canShowPlentyProducts = useStoreSelector(selectHasPlentyProduct)

  // i18n
  const { t } = useTranslation(['contracts', 'validation'])
  const language = useGetLanguage()

  // Redux queries
  const { data: productsContent, isLoading } = useGetProductContentQuery({ language })

  // React hook form
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<ProductFormInputs>({
    defaultValues: addContractData[AddContractSteps.PRODUCT],
    mode: 'onChange'
  })
  const watchElProduct = useWatch({ control, name: 'electricity' })
  const selectedProduct = getProductFromProductCode(watchElProduct)

  // Local state
  const [showDetailsFor, setShowDetailsFor] = useState<Product | undefined>(
    getProductFromProductCode(addContractData[AddContractSteps.PRODUCT]?.electricity)
  )
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState<boolean>(false)
  const [drawerProduct, setDrawerProduct] = useState<Product>(selectedProduct || PRODUCTS.variableOnline)

  // constants
  const isEarlyBird = checkIsEarlyBird()
  const hasGo = checkHasProduct([EL_PRODUCT_CODES.Go])
  const usableProducts = getUsableProducts({
    isEarlyBird,
    region: addContractData.address.region,
    userType,
    withBoltGo: hasGo,
    plentyProducts: canShowPlentyProducts
  })

  /**
   * Selects the early bird ticket if none is selected and isEarlyBird
   */
  useEffect(() => {
    const isEarlyBirdWithBoltVariableSelected =
      isEarlyBird &&
      !([EL_PRODUCT_CODES.EarlyBirdOn, EL_PRODUCT_CODES.EarlyBirdOff] as ELProduct[]).includes(watchElProduct) &&
      ([EL_PRODUCT_CODES.VariableOn, EL_PRODUCT_CODES.VariableOff] as ELProduct[]).includes(watchElProduct)

    if (isEarlyBirdWithBoltVariableSelected) {
      const products = defineProducts(
        addContractData[AddContractSteps.CUSTOMER_DATA].contactMode,
        addContractData[AddContractSteps.ADDRESS].needsGas,
        true
      )
      setValue('electricity', products.electricity)
      if (addContractData[AddContractSteps.ADDRESS].needsGas) setValue('gas', products.gas)
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  /**
   * Submit form data
   * @param {ProductFormInputs} data
   */
  const onSubmit = (data: ProductFormInputs) => {
    setNextStep(data, AddContractSteps.INSTALMENT_DATA)
  }

  return (
    <section>
      <form onSubmit={handleSubmit(onSubmit)} className={classNames(contractStyles['form-card'], styles['product-step'])}>
        <fieldset className={contractStyles['form-content']}>
          <Heading as="h1" variant="h4">
            {t(($) => $.add.steps.product.title)}
          </Heading>

          <ul className={styles.list}>
            {isLoading && usableProducts.map((product) => <li key={product} className={styles.skeleton} />)}
            {!isLoading &&
              usableProducts.map((product) => (
                <li key={product} className={classNames(styles['product-wrapper'], { [styles.selected]: selectedProduct === product })}>
                  <label className={styles.label}>
                    <input
                      type="radio"
                      value={EL_PRODUCT_CODES[product]}
                      {...register('electricity', { required: true })}
                      onClick={() => {
                        setShowDetailsFor(product)
                        if (addContractData[AddContractSteps.ADDRESS].needsGas) {
                          setValue('gas', GAS_PRODUCT_CODES[product])
                        }
                      }}
                    />
                    <div className={styles.product}>
                      <strong>{productsContent?.[product]?.name}</strong>
                      {!!billingContract?.serviceContracts?.electricity &&
                        getProductFromProductCode(billingContract.serviceContracts?.electricity.detail.productCode) === product && (
                          <Tag type="positive">{t(($) => $.add.steps.product.current)}</Tag>
                        )}
                    </div>
                  </label>

                  <Button
                    type="button"
                    leadingIcon="chevronDown"
                    variant="tertiary"
                    className={classNames(styles['dropdown-toggle'], { [styles.open]: showDetailsFor === product })}
                    onClick={() => setShowDetailsFor(showDetailsFor === product ? undefined : product)}
                  />

                  {showDetailsFor === product && (
                    <>
                      {productsContent && (productsContent?.[product]?.alternativePerks || productsContent?.[product]?.perks) && (
                        <div className={styles.perks}>
                          {addContractData.address.region !== REGIONS.flanders && productsContent?.[product]?.alternativePerks ? (
                            <BlocksRenderer content={productsContent[product].alternativePerks} />
                          ) : (
                            <BlocksRenderer content={productsContent[product].perks!} />
                          )}
                        </div>
                      )}
                      <Button
                        type="button"
                        className={styles['details-button']}
                        representation="link"
                        variant="secondary"
                        onClick={() => {
                          setDrawerProduct(product)
                          setDetailsDrawerOpen(true)
                        }}
                      >
                        {t(($) => $.common.showDetails, {
                          defaultValue: 'Toon details'
                        })}
                      </Button>
                    </>
                  )}
                </li>
              ))}
          </ul>

          {errors?.electricity?.type === 'required' && (
            <InlineMessage type="negative">
              {t(($) => $.required, {
                ns: 'validation'
              })}
            </InlineMessage>
          )}
        </fieldset>

        <FormButtons currentStep={AddContractSteps.PRODUCT} trackingId={ContractFlowTrackingTypes.ADD} />
      </form>

      <ProductDrawer
        product={drawerProduct}
        productName={productsContent?.[drawerProduct]?.name}
        userType={account?.company ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential}
        isOpen={detailsDrawerOpen}
        onRequestClose={() => setDetailsDrawerOpen(false)}
        conditions={productsContent?.[drawerProduct]?.conditions || undefined}
        needsGas={addContractData[AddContractSteps.ADDRESS].needsGas}
      />
    </section>
  )
}

export default ProductStep
